import React, { useMemo, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import NavigationTabs from "../../../distributor-settings/network-access/navigation-tabs";
import { APP_ROUTES, ROUTE_PATHS } from "../../../../constants/app.constants";
import ComingSoonChip from "../coming-soon-chip/coming-soon-chip";

const TabKeys = {
  overview: "overview",
  subscription: "subscription",
  supportAccess: "supportAccess",
  about: "about",
  members: "members",
  securityStack: "securityStack",
  stackReport: "stackReport",
  settings: "settings",
  integrations: "integrations",
  logs: "logs",
};

export const MspAccountInfoNav = () => {
  const { distributorId, mspAccountId: mspAccountIdFromParams } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Extract mspAccountId from pathname if not available from params
  const mspAccountId = useMemo(() => {
    if (mspAccountIdFromParams) return mspAccountIdFromParams;
    
    // Extract from pathname: /subscription-portal/distributors/:distributorId/msp-accounts/:mspAccountId/*
    const match = location.pathname.match(/\/msp-accounts\/([^/]+)\//);
    return match ? match[1] : null;
  }, [mspAccountIdFromParams, location.pathname]);

  const activeTab = useMemo(() => {
    const currentPath = location.pathname;
    const routes = APP_ROUTES.subscriptionPortalPages.distributorDetailsPages.mspAccountDetailsPages;
    
    if (!mspAccountId) return TabKeys.overview;
    
    for (const [tabKey, routeTemplate] of Object.entries(routes)) {
      if (typeof routeTemplate !== "string") continue;

      const routePath = routeTemplate
        .replace(":distributorId", distributorId || "")
        .replace(":mspAccountId", mspAccountId || "");
      
      // Check if current path matches the route exactly or starts with it (for nested routes)
      if (currentPath === routePath || currentPath.startsWith(routePath + "/")) {
        return tabKey;
      }
    }
    
    return TabKeys.overview;
  }, [location.pathname, distributorId, mspAccountId]);

  const getRoutePath = useCallback((routeKey) => {
    return ROUTE_PATHS.subscriptionPortalPages.distributorDetailsPages.mspAccountDetailsPages[routeKey](
      distributorId,
      mspAccountId
    );
  }, [distributorId, mspAccountId]);

  const navigationTabs = useMemo(() => [
    { 
      eventKey: TabKeys.overview, 
      label: "Overview", 
      active: activeTab === TabKeys.overview, 
      onClick: () => navigate(getRoutePath(TabKeys.overview))
    },
    { 
      eventKey: TabKeys.subscription, 
      label: "Subscription", 
      active: activeTab === TabKeys.subscription, 
      onClick: () => navigate(getRoutePath(TabKeys.subscription))
    },
    { 
      eventKey: TabKeys.integrations, 
      label: "Integrations", 
      active: activeTab === TabKeys.integrations, 
      onClick: () => navigate(getRoutePath(TabKeys.integrations))
    },
    {
      eventKey: TabKeys.members, 
      label: "Members", 
      active: activeTab === TabKeys.members, 
      onClick: () => navigate(getRoutePath(TabKeys.members))
    },
    { 
      eventKey: TabKeys.settings, 
      label: "MSP Settings", 
      active: activeTab === TabKeys.settings, 
      onClick: () => navigate(getRoutePath(TabKeys.settings))
    },
    { 
      eventKey: TabKeys.securityStack, 
      label: "Security Stack", 
      active: activeTab === TabKeys.securityStack, 
      onClick: () => navigate(getRoutePath(TabKeys.securityStack))
    },
    { 
      eventKey: TabKeys.stackReport, 
      label: "Stack Report", 
      active: activeTab === TabKeys.stackReport, 
      onClick: () => navigate(getRoutePath(TabKeys.stackReport))
    },
    { 
      eventKey: TabKeys.supportAccess, 
      label: "Support Access", 
      active: activeTab === TabKeys.supportAccess, 
      onClick: () => navigate(getRoutePath(TabKeys.supportAccess))
    },
    { 
      eventKey: TabKeys.logs, 
      label: (
        <>
          Logs <ComingSoonChip />
        </>
      ), 
      active: activeTab === TabKeys.logs, 
      onClick: () => {},
      disabled: true,
    },
  ], [activeTab, navigate, getRoutePath]);

  return (
    <div className="navs-container w-100">
      <NavigationTabs tabs={navigationTabs} />
    </div>
  );
};
